import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  PoundSterling, TrendingUp, Calendar, Clock, 
  FileText, Download, CheckCircle2, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

export default function MyEarningsPage() {
  const { api, user } = useAuth();
  const [earnings, setEarnings] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [periodFilter, setPeriodFilter] = useState('this_month');

  useEffect(() => {
    fetchEarnings();
  }, [periodFilter]);

  const fetchEarnings = async () => {
    try {
      setLoading(true);
      
      // Fetch session records which have actual earnings data
      let sessionRecordsRes = [];
      try {
        const res = await api.get('/finance/percentages/session-records');
        sessionRecordsRes = res.data.filter(record => 
          record.professional_id === user?.id
        );
      } catch (e) {
        console.log('Session records not available');
      }
      
      // Also fetch completed appointments for the current user
      const appointmentsRes = await api.get('/appointments', {
        params: { therapist_id: user?.id }
      });
      
      // Filter completed appointments
      const completedSessions = appointmentsRes.data.filter(apt => apt.status === 'completed');
      
      // Group by month for period filtering
      const now = new Date();
      const thisMonth = now.getMonth();
      const thisYear = now.getFullYear();
      
      // Filter session records by period
      let filteredRecords = sessionRecordsRes;
      let filteredAppointments = completedSessions;
      
      if (periodFilter === 'this_month') {
        filteredRecords = sessionRecordsRes.filter(s => {
          const d = new Date(s.session_date || s.created_at);
          return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
        });
        filteredAppointments = completedSessions.filter(s => {
          const d = new Date(s.date);
          return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
        });
      } else if (periodFilter === 'last_month') {
        const lastMonth = thisMonth === 0 ? 11 : thisMonth - 1;
        const lastMonthYear = thisMonth === 0 ? thisYear - 1 : thisYear;
        filteredRecords = sessionRecordsRes.filter(s => {
          const d = new Date(s.session_date || s.created_at);
          return d.getMonth() === lastMonth && d.getFullYear() === lastMonthYear;
        });
        filteredAppointments = completedSessions.filter(s => {
          const d = new Date(s.date);
          return d.getMonth() === lastMonth && d.getFullYear() === lastMonthYear;
        });
      } else if (periodFilter === 'this_year') {
        filteredRecords = sessionRecordsRes.filter(s => {
          const d = new Date(s.session_date || s.created_at);
          return d.getFullYear() === thisYear;
        });
        filteredAppointments = completedSessions.filter(s => {
          const d = new Date(s.date);
          return d.getFullYear() === thisYear;
        });
      }
      
      // Calculate earnings from session records (which have actual splits)
      const recordsEarnings = filteredRecords.reduce((sum, record) => {
        const therapistSplit = record.splits?.find(s => s.recipient_type === 'therapist');
        return sum + (therapistSplit?.amount || 0);
      }, 0);
      
      const totalRecordsEarnings = sessionRecordsRes.reduce((sum, record) => {
        const therapistSplit = record.splits?.find(s => s.recipient_type === 'therapist');
        return sum + (therapistSplit?.amount || 0);
      }, 0);
      
      // Combine data for display
      const combinedSessions = [
        ...filteredRecords.map(r => ({
          id: r.id,
          date: r.session_date,
          patient_name: r.patient_name,
          appointment_type: r.session_type,
          start_time: r.start_time || '-',
          end_time: r.end_time || '-',
          earnings: r.splits?.find(s => s.recipient_type === 'therapist')?.amount || 0,
          total_amount: r.total_amount,
          source: 'session_record'
        })),
        ...filteredAppointments
          .filter(apt => !filteredRecords.some(r => r.patient_name === apt.patient_name && r.session_date === apt.date))
          .map(apt => ({
            id: apt.id,
            date: apt.date,
            patient_name: apt.patient_name,
            appointment_type: apt.appointment_type,
            start_time: apt.start_time,
            end_time: apt.end_time,
            earnings: 45, // Default rate for appointments without session records
            total_amount: 70,
            source: 'appointment'
          }))
      ];
      
      const periodEarnings = recordsEarnings > 0 ? recordsEarnings : filteredAppointments.length * 45;
      const totalEarnings = totalRecordsEarnings > 0 ? totalRecordsEarnings : completedSessions.length * 45;
      
      setEarnings({
        total_sessions: sessionRecordsRes.length || completedSessions.length,
        total_earnings: totalEarnings,
        period_sessions: combinedSessions.length,
        period_earnings: periodEarnings,
        has_session_records: sessionRecordsRes.length > 0
      });
      setSessions(combinedSessions.sort((a, b) => new Date(b.date) - new Date(a.date)));
    } catch (error) {
      console.error('Failed to fetch earnings:', error);
      toast.error('Failed to load earnings data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#1e3a5f] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1e3a5f]">My Earnings</h1>
          <p className="text-slate-500 mt-1">Track your sessions and earnings</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={periodFilter} onValueChange={setPeriodFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="this_month">This Month</SelectItem>
              <SelectItem value="last_month">Last Month</SelectItem>
              <SelectItem value="this_year">This Year</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="card-icrcl">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Period Earnings</p>
                <p className="text-2xl font-bold text-[#1e3a5f]">
                  £{earnings?.period_earnings?.toLocaleString('en-GB', { minimumFractionDigits: 2 }) || '0.00'}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <PoundSterling className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-icrcl">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Sessions Completed</p>
                <p className="text-2xl font-bold text-[#1e3a5f]">{earnings?.period_sessions || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-icrcl">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Average per Session</p>
                <p className="text-2xl font-bold text-[#1e3a5f]">
                  £{earnings?.period_sessions > 0 
                    ? (earnings?.period_earnings / earnings?.period_sessions).toFixed(2) 
                    : '0.00'}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <Clock className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-icrcl">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total All Time</p>
                <p className="text-2xl font-bold text-[#1e3a5f]">
                  £{earnings?.total_earnings?.toLocaleString('en-GB', { minimumFractionDigits: 2 }) || '0.00'}
                </p>
              </div>
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sessions Table */}
      <Card className="card-icrcl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#2a9d8f]" />
            Completed Sessions
          </CardTitle>
          <CardDescription>Your completed therapy sessions for the selected period</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {sessions.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <AlertCircle className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No completed sessions found for this period</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Patient</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead>Earnings</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sessions.map((session) => (
                  <TableRow key={session.id}>
                    <TableCell className="font-medium">
                      {new Date(session.date).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </TableCell>
                    <TableCell>{session.patient_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {session.appointment_type?.replace(/_/g, ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-600">
                      {session.start_time} - {session.end_time}
                    </TableCell>
                    <TableCell className="font-semibold text-green-600">
                      £{session.earnings?.toFixed(2) || '0.00'}
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-green-100 text-green-800">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        {session.source === 'session_record' ? 'Recorded' : 'Completed'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Note */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Earnings are calculated based on completed sessions. 
                For detailed payslip information, please contact the Finance department.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
